/**
 * A minimal reader for the Server-Sent Events wire format — just enough for our
 * realtime stream. The browser readers (consumer + dealership-dashboard) feed it
 * decoded text chunks and get back the `data:` payloads of completed events. It
 * lives here, shared, so both apps frame the wire identically and the (stateful,
 * chunk-spanning) buffering is unit-tested in one place rather than duplicated.
 *
 * It is intentionally partial: our endpoints only ever send `data:` frames (plus
 * ": " connect/keep-alive comments), so comments and any other field (`event:`,
 * `id:`, `retry:`) are skipped. Per the SSE spec an event is dispatched on the
 * blank line that terminates its block, and multiple `data:` lines in one block
 * are joined with newlines.
 */

const DATA_FIELD = 'data:';

/**
 * Create a stateful SSE reader. `push` takes the next decoded text chunk and
 * returns the `data:` payload of every event that chunk completed; a partial line
 * or a half-received block is buffered until the bytes that finish it arrive, so
 * events split across network chunks are reassembled correctly.
 */
export const createSseParser = (): { push: (chunk: string) => string[] } => {
  let buffer = '';
  let data: string | undefined;

  return {
    push: (chunk: string): string[] => {
      buffer += chunk;
      const payloads: string[] = [];

      const lines = buffer.split('\n');
      // Keep the trailing (possibly incomplete) line for the next chunk.
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        // A blank line terminates the current event block: emit its data, if any.
        if (line.length === 0) {
          if (data !== undefined) {
            payloads.push(data);
            data = undefined;
          }
          continue;
        }
        // Comments (": connected", keep-alive pings) carry no data.
        if (line.startsWith(':')) {
          continue;
        }
        if (line.startsWith(DATA_FIELD)) {
          const value = line.slice(DATA_FIELD.length).trim();
          data = data === undefined ? value : `${data}\n${value}`;
          continue;
        }
        // Any other field (event:, id:, retry:) is not produced by our endpoints.
      }

      return payloads;
    }
  };
};
